import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", client_id: "", tax_rate: 0, notes: "", due_date: "", status: "draft", items: [{ description: "", quantity: 1, unit_price: 0 }] };

function ItemsEditor({ items, setItems }) {
  const update = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: k === "description" ? v : Number(v || 0) } : it));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { _key: Math.random().toString(36).slice(2), description: "", quantity: 1, unit_price: 0 }]);
  return (
    <div className="md:col-span-2">
      <div className="overline mb-2">Line items</div>
      <div className="rounded-md border border-zinc-800 divide-y divide-zinc-800">
        {items.map((it, i) => (
          <div key={it._key || `${i}-${it.description || ""}`} className="grid grid-cols-12 gap-2 p-2">
            <input placeholder="Description" value={it.description} onChange={(e) => update(i, "description", e.target.value)} className="col-span-6 h-8 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs" />
            <input placeholder="Qty" type="number" value={it.quantity} onChange={(e) => update(i, "quantity", e.target.value)} className="col-span-2 h-8 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs" />
            <input placeholder="Unit price" type="number" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} className="col-span-3 h-8 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs" />
            <button onClick={() => remove(i)} className="col-span-1 h-8 grid place-items-center rounded text-zinc-400 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><Plus className="w-3 h-3" /> Add item</button>
    </div>
  );
}

export default function Invoices({ portal = false }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [i, c] = await Promise.all([
      api.get("/invoices"),
      portal ? Promise.resolve({ data: [] }) : api.get("/crm/clients").catch(() => ({ data: [] })),
    ]);
    setItems(i.data);
    setClients(c.data);
  }, [portal]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form, tax_rate: Number(form.tax_rate || 0) };
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      await api.post("/invoices", payload);
      toast.success(t("common.create")); setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (iid) => {
    if (!window.confirm(t("common.confirm") + "?")) return;
    try { await api.delete(`/invoices/${iid}`); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const pay = async (iid) => {
    try {
      const { data } = await api.post(`/invoices/${iid}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const canCreate = !portal && user?.role !== "client";

  return (
    <DashboardLayout title={t("invoices.title")} subtitle={t("invoices.subtitle")}
      right={canCreate && <button data-testid="invoices-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> {t("invoices.new")}</button>}>
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="invoices-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">Title</th><th className="p-3">Client</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Due</th><th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{inv.title}</td>
                <td className="p-3 text-zinc-400 text-xs">{clients.find(c => c.client_id === inv.client_id)?.name || "—"}</td>
                <td className="p-3 text-zinc-400">${Number(inv.total || 0).toFixed(2)}</td>
                <td className="p-3"><span className={`text-xs ${inv.status === "paid" ? "text-emerald-400" : inv.status === "overdue" ? "text-rose-400" : "text-blue-400"}`}>{t(`invoices.statuses.${inv.status}`)}</span></td>
                <td className="p-3 text-zinc-500 text-xs">{inv.due_date || "—"}</td>
                <td className="p-3 text-right">
                  {inv.status !== "paid" && (
                    <button data-testid={`invoice-pay-${inv.invoice_id}`} onClick={() => pay(inv.invoice_id)} className="btn-scale h-8 px-2 rounded-md bg-blue-500 text-white hover:bg-blue-400 inline-flex items-center gap-1 text-xs"><CreditCard className="w-3 h-3" /> {t("invoices.pay")}</button>
                  )}
                  {canCreate && (
                    <button data-testid={`invoice-del-${inv.invoice_id}`} onClick={() => remove(inv.invoice_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800 inline-flex ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">{t("common.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {canCreate && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl" data-testid="invoice-dialog">
            <DialogHeader><DialogTitle>{t("invoices.new")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <label><span className="overline block mb-1.5">Title</span>
                <input data-testid="invoice-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
              </label>
              <label><span className="overline block mb-1.5">Client</span>
                <select data-testid="invoice-field-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                  <option value="">—</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </label>
              <label><span className="overline block mb-1.5">Tax rate (%)</span>
                <input data-testid="invoice-field-tax" type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
              </label>
              <label><span className="overline block mb-1.5">Due date</span>
                <input data-testid="invoice-field-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
              </label>
              <ItemsEditor items={form.items} setItems={(items) => setForm({ ...form, items })} />
            </div>
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
              <button data-testid="invoice-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
